// src/web/client/src/components/subscription/SubscriptionGuard.jsx
// קומפוננטה שמאזינה לאירועי מנוי ומציגה הודעות מתאימות

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authenticatedFetch } from "@/utils/authenticatedFetch";
import {
  hasFeature as planHasFeature,
  hasAnyFeature,
  planBadgeLabel,
} from '@/utils/planEntitlements';
/**
 * SubscriptionGuard
 * עוטף את האפליקציה ומאזין לאירועי מנוי מה-API
 * מציג modal כשצריך לשדרג Plan
 */
const SubscriptionGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  // Populated when ANY screen hits a 402 (handlers in authenticatedFetch.js
  // and api.js both dispatch subscription:required). Rendering it here as a
  // full-screen blocking overlay means we don't have to teach every screen
  // to catch SubscriptionRequiredError individually — the same friendly
  // "choose a plan" CTA shows up consistently everywhere.
  const [subscriptionBlock, setSubscriptionBlock] = useState(null);

  // Clear the overlay when the user navigates onto /billing — they're now
  // in the right place to fix the problem; pinning it open would be hostile.
  useEffect(() => {
    if (location.pathname === '/billing' && subscriptionBlock) {
      setSubscriptionBlock(null);
    }
  }, [location.pathname, subscriptionBlock]);

  useEffect(() => {
    // האזנה לאירוע "מנוי נדרש" (402)
    const handleSubscriptionRequired = (event) => {
      console.log('[SubscriptionGuard] Subscription required:', event.detail);
      setSubscriptionBlock({
        code: event.detail?.code,
        message: event.detail?.message,
        subscriptionStatus: event.detail?.subscriptionStatus
      });
    };

    // האזנה לאירוע "פיצ'ר לא זמין" (403)
    const handleUpgradeNeeded = (event) => {
      console.log('[SubscriptionGuard] Upgrade needed:', event.detail);
      setUpgradeInfo(event.detail);
      setShowUpgradeModal(true);
    };

    // האזנה לאירוע "נדרש התחברות" (401)
    const handleAuthRequired = () => {
      console.log('[SubscriptionGuard] Auth required');
      navigate('/login');
    };

    // רישום האזנות
    window.addEventListener('subscription:required', handleSubscriptionRequired);
    window.addEventListener('subscription:upgrade-needed', handleUpgradeNeeded);
    window.addEventListener('auth:required', handleAuthRequired);

    // ניקוי בעת unmount
    return () => {
      window.removeEventListener('subscription:required', handleSubscriptionRequired);
      window.removeEventListener('subscription:upgrade-needed', handleUpgradeNeeded);
      window.removeEventListener('auth:required', handleAuthRequired);
    };
  }, [navigate]);

  // סגירת Modal
  const closeModal = () => {
    setShowUpgradeModal(false);
    setUpgradeInfo(null);
  };

  // מעבר לדף Billing
  const goToBilling = () => {
    closeModal();
    navigate('/billing');
  };

  const goToBillingFromBlock = () => {
    setSubscriptionBlock(null);
    navigate('/billing');
  };

  const subscriptionBlockTitle = (() => {
    if (!subscriptionBlock) return '';
    if (subscriptionBlock.code === 'TRIAL_EXPIRED') return 'Your free trial has ended';
    if (subscriptionBlock.code === 'SUBSCRIPTION_CANCELLED') return 'Your subscription was cancelled';
    return 'Choose a plan to continue';
  })();

  return (
    <>
      {children}

      {/* Subscription-required full-screen block — shown whenever any API
          call gets a 402. Matches the look of the Orders inline block so
          the experience is identical on every screen. Hidden on /billing
          itself because the user is already in the right place to fix it. */}
      {subscriptionBlock && location.pathname !== '/billing' && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50 bg-opacity-95"
        >
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-8 text-center">
            <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {subscriptionBlockTitle}
            </h2>
            <p className="text-gray-600 mb-6">
              {subscriptionBlock.message ||
                'Pick a plan to start your 90-day free trial and unlock the app.'}
            </p>
            <button
              onClick={goToBillingFromBlock}
              className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              Go to Billing
            </button>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && upgradeInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                <svg 
                  className="w-8 h-8 text-purple-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M13 10V3L4 14h7v7l9-11h-7z" 
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-semibold text-center text-gray-900 mb-2">
              Upgrade Required
            </h3>

            {/* Message */}
            <p className="text-center text-gray-600 mb-4">
              {upgradeInfo.message || 'This feature requires a higher plan.'}
            </p>

            {/* Feature Info */}
            {upgradeInfo.feature && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Feature:</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {upgradeInfo.feature.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-gray-500">Required Plan:</span>
                  <span className="text-sm font-medium text-purple-600 capitalize">
                    {upgradeInfo.requiredPlan || 'Pro'}
                  </span>
                </div>
              </div>
            )}

            {/* Plan Benefits */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-2">
                With {upgradeInfo.requiredPlan || 'Pro'} Plan you get:
              </p>
              <ul className="space-y-1">
                {getPlanBenefits(upgradeInfo.requiredPlan).map((benefit, index) => (
                  <li key={index} className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={goToBilling}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * החזרת יתרונות לפי Plan
 */
function getPlanBenefits(plan) {
  const benefits = {
    basic: [
      '1,000 messages per month',
      '5 message templates',
      'Full analytics dashboard',
      'Data export'
    ],
    pro: [
      'Unlimited messages',
      'Unlimited templates',
'Advanced Personalization',
      'VIP Customer Detection',
      'Full API Access',
      'Priority Support'
    ]
  };

  return benefits[plan] || benefits.pro;
}

export default SubscriptionGuard;


// ============================================
// HOOK: useSubscriptionStatus
// ============================================

/**
 * Hook לבדיקת סטטוס מנוי
 * שימושי בקומפוננטות שרוצות להציג/להסתיר פיצ'רים לפי Plan
 */
export const useSubscriptionStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await authenticatedFetch(
          `${import.meta.env.VITE_API_BASE_URL || 'https://api.ecomoria.com/api'}/subscription/info?shop_domain=${localStorage.getItem('shop_domain')}`,
          {
            headers: {
              'X-Shop-Domain': localStorage.getItem('shop_domain')
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setStatus(data);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  // Helper functions
  const isPro = status?.plan === 'pro';
  const isBasic = status?.plan === 'basic';
  const isTrial = status?.plan === 'trial';
  const isActive = status?.status === 'active' || (status?.trial_active);

  // Entitlement now comes from the server's `features` map (the backend's
  // config/plans.js FEATURE_ACCESS projection), not from a plan-string
  // ternary.
  //
  // The old implementation was `isPro ? true : isBasic ? [analytics,
  // export_data] : false` — a second, hand-maintained copy of the entitlement
  // table that had already drifted from the real one (it withheld
  // revenue_attribution from Basic, which plans.js grants) and that answered
  // FALSE FOR EVERY FEATURE on the `scale` tier, because scale is neither the
  // string 'pro' nor the string 'basic'.
  //
  // Nothing calls this today — Layout, the hook's only consumer, uses the
  // booleans below — so no merchant's access changes on this line. It is fixed
  // rather than deleted because it is exported from a shared hook and is
  // exactly the kind of helper the next screen would reach for.
  const hasFeature = (feature) => planHasFeature(status, feature);

  return {
    status,
    loading,
    error,
    isPro,
    isBasic,
    isTrial,
    isActive,
    hasFeature,
    // Config-derived answers for the two things Layout actually gates on.
    // `isPro` above stays as-is: it is a question about WHICH PLAN, and the
    // Billing screen still legitimately asks it. It is no longer used to
    // answer "may this merchant use X".
    hasFeatureAccess: (feature) => planHasFeature(status, feature),
    hasAnyFeatureAccess: (features) => hasAnyFeature(status, features),
    planLabel: planBadgeLabel(status),
    messagesRemaining: status?.messages_remaining || 0,
    trialDaysLeft: status?.trial_days_left || 0
  };
};